import { useState } from 'react'
import { cn } from '@app/lib/cn'
import { imageUrl } from '@app/lib/images'

/**
 * The product photo — every place one appears, from a 32px document line to the
 * hero on a stock card.
 *
 * Korgun shows these photos in a modal you have to ask for; here the picture is
 * simply part of the row, because "is this the black one with the thick sole?"
 * is the question a code never answers. The API hands over an R2 object key
 * (`imageKey`, derived from Korgun's stored path by worker/lib/images.ts) and
 * this component turns it into a picture or into the absence of one.
 *
 * The absence is the interesting half, and for now it is the ONLY half — the
 * bucket is filled by hand, so until it is, `lib/images.ts` keeps the base URL
 * null and every one of these is a placeholder. So the placeholder is not a
 * grey box waiting to be replaced: it is a drawn line glyph on the app's own
 * paper ground, a shoe in side profile for a finished product and a stack of
 * cut sheets for a material, quiet enough (ink-muted at 40%) that a column of
 * them reads as texture rather than as fifty missing images. Both are drawn
 * here rather than borrowed from the icon set — lucide's picture-frame icon
 * says "image missing", which is a fact about our data pipeline; a shoe says
 * "shoe", which is what the row is about.
 *
 * A photo that fails to load falls back to the same glyph, so a key whose
 * object never made it into the bucket (or was uploaded under a different case)
 * degrades exactly like a card that never had a photo.
 */

export type ProductImageKind = 'shoe' | 'material'
export type ProductImageSize = 'thumb' | 'line' | 'hero'

interface ProductImageProps {
  /** R2 object key from the API; null wherever Korgun has no photo on file. */
  imageKey: string | null | undefined
  /** Turkish. The row already prints the code, so name the thing, not the file. */
  alt: string
  /** Which glyph stands in for a missing photo. */
  kind?: ProductImageKind
  size?: ProductImageSize
  className?: string
}

/**
 * Photo and placeholder share the frame, so a row's rhythm does not depend on
 * whether a picture happens to exist. `bg-kagit` is also what letterboxes a
 * hero that does not fill its 4:3 box.
 */
const FRAME = 'shrink-0 overflow-hidden rounded-xs border border-hairline bg-kagit'

/**
 * `thumb` sits in list rows, `line` in the denser document-line tables, `hero`
 * beside a detail page's heading — where the caller sets the width and this
 * only fixes the proportion.
 */
const BOX: Record<ProductImageSize, string> = {
  thumb: 'size-9',
  line: 'size-8',
  hero: 'aspect-[4/3] w-full max-w-[260px] rounded-sm',
}

const GLYPH: Record<ProductImageSize, string> = {
  thumb: 'size-5',
  line: 'size-4',
  hero: 'size-10',
}

export function ProductImage({
  imageKey,
  alt,
  kind = 'shoe',
  size = 'thumb',
  className,
}: ProductImageProps) {
  /*
   * WHICH key failed, not whether one did. Wherever a row keeps its React key
   * across a data change (a filter narrowing the list, a colour row re-pointed
   * by the depot switch on the stock card), this component is handed a new
   * `imageKey` without remounting — and a boolean would keep the new photo
   * hidden behind the old one's 404.
   */
  const [brokenKey, setBrokenKey] = useState<string | null>(null)
  const url = imageKey ? imageUrl(imageKey) : null

  if (url === null || imageKey === brokenKey) {
    return (
      // Decorative: everything the placeholder could announce is already in the
      // row next to it, and fifty "fotoğraf yok" in a screen reader is noise.
      <span aria-hidden className={cn(FRAME, 'grid place-items-center', BOX[size], className)}>
        <PlaceholderGlyph kind={kind} className={cn('text-ink-muted/40', GLYPH[size])} />
      </span>
    )
  }

  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => setBrokenKey(imageKey ?? null)}
      className={cn(
        FRAME,
        // Thumbs are read as swatches, so they may crop; a hero never crops the
        // shoe — the toe shape is half of what the picture is being asked.
        size === 'hero' ? 'object-contain' : 'object-cover',
        BOX[size],
        className,
      )}
    />
  )
}

/**
 * One 24×24 line drawing per kind, at a single hairline weight and in
 * `currentColor`, so both themes come free from the token that colours them.
 *
 * Shoe: a side profile pointing left. The dip between the vamp and the heel
 * counter is what makes it a shoe rather than a hill — that notch is the ankle
 * opening, and without it the outline reads as a doorstop at 20px. The outsole
 * is one straight rule the upper stands on, running past both the toe and the
 * heel the way a real sole does.
 * Material: a cut sheet seen from above with two more stacked under it — how
 * leather, lining and sole board actually sit in the cutting room. Checked at
 * 16px, which is the smallest place either of them is drawn.
 */
function PlaceholderGlyph({ kind, className }: { kind: ProductImageKind; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      {kind === 'shoe' ? (
        <>
          <path d="M4.6 16.2c0-1.7 1-2.9 2.5-3.2l4.4-1c.9-.2 1.8-.7 2.5-1.5.6 1.2 1.5 1.6 2.3 1.2 1-.5 1.6-1.9 2-3.6.2-.8 1.3-.7 1.5.1.3 1.6.4 3.2.4 4.8v3.2" />
          <path d="M3.4 16.2h17.8" />
        </>
      ) : (
        <>
          <path d="M12 6.6 20.6 9.4 12 12.2 3.4 9.4Z" />
          <path d="M4.4 11.9 12 14.4l7.6-2.5" />
          <path d="M4.4 14.4 12 16.9l7.6-2.5" />
        </>
      )}
    </svg>
  )
}
